import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import { Layers, Trash, User } from "react-feather";
import { startOfMonth, subYears } from "date-fns";
import {
  autoLinker,
  convertToHumanReadable,
  convertWeightToHumanReadable,
  formatDate,
} from "@/Utilities/Utilities";
import { AccordionItem, Formheader, Pagination } from "@/Components";
import { Inertia } from "@inertiajs/inertia";
import { toast } from "react-toastify";
import { usePage } from "@inertiajs/inertia-react";
import { QUESTION_TYPES } from "@/Enums/question_types";
import { useCalculateEmissionValues } from "@/Utilities/Hooks";
import { useAtomValue } from "jotai";
import { generalFilterAtom } from "@/Stores";
import { ROLE_ID } from "@/Enums/roles";

const HistoryItem = ({
  item,
  question,
  type,
  permissionDelete = false,
  onDelete = (_) => null,
}) => {
  const getAuthorName = () => {
    if (!item?.author) return "Unknown or deleted user";

    if (type === "webhook") return `Automatic entry from ${item.author.name}`;
    if (type === "user")
      return `${item.author.firstname} ${item.author.surname.charAt(0)}`;
    if (type === "calculator_recipient")
      return `${item.author.name} ${item.author.email}`;

    return "Unknown or deleted user";
  };

  const getInputById = (id) => question.inputs.find((input) => input.id === id);

  const deleteAnswer = () => {
    if (!permissionDelete) return;

    const id = toast.loading("Please wait...");

    Inertia.delete(`/questions/answers/${item.id}`, {
      preserveScroll: true,
      only: ["questionTypes"],
      onSuccess: () => {
        toast.update(id, {
          render: "The answer has been deleted",
          type: toast.TYPE.SUCCESS,
          isLoading: false,
          autoClose: 5000,
        });
        onDelete(item.id);
      },
      onError: (error) => {
        toast.update(id, {
          render: "The answer could not be deleted!",
          type: toast.TYPE.ERROR,
          isLoading: false,
          autoClose: 5000,
        });
      },
    });
  };

  const common = item.values?.[0];

  if (!common) return null;

  const meta = {
    author: getAuthorName(),
    reportedDate: formatDate(item.reported_date),
    startDate: formatDate(item.start_date),
    endDate:
      item.start_date !== item.end_date ? formatDate(item.end_date) : null,
    comment: item.comment,
    label: null,
  };

  switch (question.type.enum) {
    case "calculate":
      meta.label = `${convertToHumanReadable(common.value)} ${
        common.factor.unit
      }`;
      break;
    case "select":
      if (question?.meta?.select_type !== "multi") {
        const input = getInputById(common.question_input_id);
        meta.label = input.label;

        if (input.unit) {
          meta.label += `, ${convertToHumanReadable(common.value)} ${
            input.unit
          }`;
        }
      } else {
        const hasValue =
          question?.meta?.require_value ||
          question.inputs.some((input) => input.emission_factor_id !== null);

        meta.label = item.values
          .map((item) => {
            const input = getInputById(item.question_input_id);

            const formattedLabel = `${input.label}${
              hasValue ? `: ${convertToHumanReadable(item.value)}` : ""
            }`;
            const formattedUnit = input.unit ? ` ${input.unit}` : "";

            return formattedLabel + formattedUnit;
          })
          .join(", ");
      }
      break;
    case "free_text":
      meta.label = common.value;
      break;
    case "historical":
    case "distribute":
      meta.label = "(Distribute)";
      meta.label = item.values
        .map((item) => {
          const input = getInputById(item.question_input_id);
          return `${input.label}: ${convertToHumanReadable(item.value)}${
            !input.unit ? "%" : ` ${input.unit}`
          }`;
        })
        .join(", ");
      break;
  }

  return (
    <div className="mb-4 flex items-start border-b last:mb-0 last:border-none">
      <div className="flex grow flex-col">
        <p className="font-bold">
          {/*<span className="mr-2 text-sm font-normal">{meta.reportedDate}</span>*/}
          <span dangerouslySetInnerHTML={{ __html: meta.label }}></span>
        </p>
        <p
          className="autolinker"
          dangerouslySetInnerHTML={{ __html: autoLinker(meta.comment) }}
        />
        <div className="mb-4 mt-2">
          <span className="mr-2 text-sm">
            {meta.startDate}
            {meta.endDate ? ` - ${meta.endDate}` : null}
          </span>
          <span className="text-sm text-typo-inactive">{meta.author}</span>
        </div>
      </div>
      {permissionDelete && (
        <button
          className="hover-circle relative shrink-0"
          onClick={deleteAnswer}
        >
          <Trash stroke="#363636" className="cursor-pointer" />
        </button>
      )}
    </div>
  );
};

const HistoryTitle = ({ label, question, history }) => {
  if (history.length === 0) return label;

  const generalFilter = useAtomValue(generalFilterAtom);

  if (
    question.type.enum === QUESTION_TYPES.Calculate ||
    question.type.enum === QUESTION_TYPES.Historical ||
    question.type.enum === QUESTION_TYPES.Select
  ) {
    const values = history.map((item) => item.values).flat();
    const factor = values[0]?.factor;

    if (!factor) return label;

    // Combine all history values into one array, then get the total emission
    const emission = useCalculateEmissionValues(
      values,
      generalFilter?.format || "default",
    );
    const total = values.reduce((acc, item) => acc + item.value, 0);

    return (
      <>
        <span>Total reported</span>
        <span className="ml-2 font-bold">
          {convertToHumanReadable(total)} {factor.unit}
        </span>
        <span className="ml-2 rounded-md bg-blue-lighter px-3 py-2 text-sm leading-none">
          {convertWeightToHumanReadable(emission.total)?.concat} CO₂e
        </span>
      </>
    );
  }

  return label;
};

export const ReportHistory = forwardRef(
  ({ question, team, type = "user" }, ref) => {
    // const team = usePage().props?.team?.data || {};

    const user = usePage().props?.auth?.user?.data || {};

    const isOwner = [
      ROLE_ID.SUPER,
      ROLE_ID.GLOBAL_REPORTER,
      ROLE_ID.OWNER,
    ].includes(user.organization_role_id);

    const [history, setHistory] = useState([]);
    const [updatedFlash, setUpdatedFlash] = useState(false);

    const timer = useRef(null);

    const meta = {
      user: {
        label: (
          <HistoryTitle
            label="Reporters"
            history={history}
            question={question}
          />
        ),
      },
      calculator_recipient: {
        label: (
          <HistoryTitle
            label="Calculators"
            history={history}
            question={question}
          />
        ),
      },
      webhook: {
        label: (
          <HistoryTitle
            label="Webhooks"
            history={history}
            question={question}
          />
        ),
      },
    };

    useImperativeHandle(ref, () => {
      return {
        refreshHistory() {
          fetchHistory().then(() => {
            setUpdatedFlash(true);
            timer.current = setTimeout(() => {
              setUpdatedFlash(false);
            }, 300);
          });
        },
      };
    }, []);

    useEffect(() => {
      fetchHistory();

      return () => {
        if (timer.current) {
          clearTimeout(timer.current);
        }
      };
    }, []);

    const fetchHistory = () => {
      const twelveMonthsAgo = startOfMonth(subYears(new Date(), 1));

      const filterParams = new URLSearchParams({
        author_type: type,
        reported_after: formatDate(twelveMonthsAgo.toISOString(), "yyyy-MM-dd"),
        team_id: team.id,
      });

      return fetch(
        `/questions/${question.id}/answers/grouped?${filterParams.toString()}`,
      )
        .then((response) => response.json())
        .then((data) => {
          setHistory(data);
        });
    };

    if (!history.length) {
      return (
        <div className="flex flex-col bg-gray-light px-8 pb-8 pt-4 shadow-small">
          <Formheader>No reports available</Formheader>
          <div>
            No reports was found within the last 12 months for{" "}
            {meta[type].label}
          </div>
        </div>
      );
    }

    return (
      <div className="flex flex-col bg-gray-light px-8 pt-4 shadow-small">
        <Formheader>Reported last 12 months</Formheader>

        <div>{meta[type].label}</div>

        {/*<AccordionItem*/}
        {/*  title={meta[type].label}*/}
        {/*  icon={meta[type].icon}*/}
        {/*  titleStyle="font-bold mt-1"*/}
        {/*  background={updatedFlash ? "bg-[#FEE9E5]" : "bg-gray-light"}*/}
        {/*  className="shadow duration-150"*/}
        {/*>*/}
        <Pagination
          itemsPerPage={5}
          items={history.map((item) => (
            <HistoryItem
              key={item.id}
              item={item}
              question={question}
              type={type}
              permissionDelete={isOwner}
              onDelete={(id) => {
                setHistory((prev) => {
                  const index = prev.findIndex((item) => item.id === id);
                  prev.splice(index, 1);
                  return [...prev];
                });
              }}
            />
          ))}
        />
        {/*</AccordionItem>*/}
      </div>
    );
  },
);
